import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { Session } from '@wharfkit/session';
import { getSessionKit } from '../wax/session';

interface WaxContextType {
  waxSession: Session | null;
  waxActor: string;
  waxConnecting: boolean;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
}

const WaxContext = createContext<WaxContextType | undefined>(undefined);

export const WaxProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [waxSession, setWaxSession] = useState<Session | null>(null);
  const [waxActor, setWaxActor] = useState('');
  const [waxConnecting, setWaxConnecting] = useState(false);
  const restored = useRef(false);

  useEffect(() => {
    if (restored.current) return;
    restored.current = true;
    getSessionKit().restore().then((s) => {
      if (s) {
        const actor = String(s.actor);
        setWaxSession(s as Session);
        setWaxActor(actor);
        localStorage.setItem('agrivoled_wax_account', actor);
      }
    }).catch(() => {});
  }, []);

  const connect = async () => {
    setWaxConnecting(true);
    try {
      const { session: s } = await getSessionKit().login();
      const actor = String(s.actor);
      setWaxSession(s as Session);
      setWaxActor(actor);
      localStorage.setItem('agrivoled_wax_account', actor);
    } finally {
      setWaxConnecting(false);
    }
  };

  const disconnect = async () => {
    if (waxSession) await getSessionKit().logout(waxSession);
    setWaxSession(null);
    setWaxActor('');
    localStorage.removeItem('agrivoled_wax_account');
  };

  return (
    <WaxContext.Provider value={{ waxSession, waxActor, waxConnecting, connect, disconnect }}>
      {children}
    </WaxContext.Provider>
  );
};

export const useWax = () => {
  const context = useContext(WaxContext);
  if (!context) throw new Error('useWax must be used within WaxProvider');
  return context;
};
